import React from 'react';
import { Question, OASISFormData, FormValue } from './types';
import { RadioQuestion } from './questions/RadioQuestion';
import { TextQuestion } from './questions/TextQuestion';
import { DateQuestion } from './questions/DateQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { NumberQuestion } from './questions/NumberQuestion';

interface OASISQuestionRendererProps {
  question: Question;
  value: FormValue;
  onChange: (value: FormValue) => void;
  formData: OASISFormData;
  onFormDataChange?: (updates: Partial<OASISFormData>) => void;
}

export const OASISQuestionRenderer: React.FC<OASISQuestionRendererProps> = ({
  question,
  value,
  onChange,
  formData,
  onFormDataChange,
}) => {
  const isPseudo = question.is_pseudo === true;

  // For pure pseudo questions with "Yes" subitems, auto-select "Yes" and render subitems directly
  const effectiveValue = isPseudo && question.subitems?.['Yes'] ? 'Yes' : value;

  // Render subitems if they exist and the parent has a matching value
  const renderSubitems = (subitemValue: FormValue) => {
    if (!question.subitems || !subitemValue) return null;

    // Only string values can be used as keys for subitems
    if (typeof subitemValue !== 'string') return null;

    const subitems = question.subitems[subitemValue];
    if (!subitems) return null;

    // For pure pseudo questions, render subitems without indentation
    const wrapperClass = isPseudo
      ? 'space-y-4'
      : 'ml-6 mt-4 space-y-4 border-l-2 border-gray-200 pl-4';

    return (
      <div className={wrapperClass}>
        {subitems.map((subitem: Question) => (
          <OASISQuestionRenderer
            key={subitem.qid}
            question={subitem}
            value={formData[subitem.qid]}
            onChange={(subValue: FormValue) => {
              if (onFormDataChange) {
                const updates: Partial<OASISFormData> = {
                  [subitem.qid]: subValue,
                };
                // If parent is a pseudo question with "Yes" option, set it to "Yes" when subitem changes
                if (isPseudo && question.subitems?.['Yes']) {
                  updates[question.qid] = 'Yes';
                }
                onFormDataChange(updates);
              } else {
                onChange(subValue);
              }
            }}
            formData={formData}
            onFormDataChange={onFormDataChange}
          />
        ))}
      </div>
    );
  };

  const renderQuestion = () => {
    switch (question.type) {
      case 'radio':
        return (
          <RadioQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        );

      case 'text':
        return (
          <TextQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        );

      case 'number':
        return (
          <NumberQuestion
            question={question}
            value={
              typeof value === 'number'
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
          />
        );

      case 'date':
        return (
          <DateQuestion
            question={question}
            value={
              value instanceof Date
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
          />
        );

      case 'multiselect':
        return (
          <MultiSelectQuestion
            question={question}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
          />
        );

      default:
        return (
          <div className='text-red-600'>
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  // For pseudo questions with "Yes" subitems, render subitems directly (no toggle/separator)
  if (isPseudo && question.subitems?.['Yes']) {
    return <>{renderSubitems('Yes')}</>;
  }

  return (
    <div className='space-y-2'>
      {renderQuestion()}
      {renderSubitems(effectiveValue)}
    </div>
  );
};
